import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faCheck,
  faStar,
  faTrash,
  faUpload,
  faSpinner,
  faRotate,
  faMagnifyingGlass
} from '@fortawesome/free-solid-svg-icons';
import { UiButtonComponent } from '../../shared/ui/button/ui-button.component';
import { UiModalComponent } from '../../shared/ui/modal/ui-modal.component';
import { RecipeImage, RecipeImageService } from '../../services/recipe/recipe-image.service';
import {
  ImportImageSelectionDto,
  RecipeImportJobDto,
  RecipeImportService
} from '../../services/recipe/recipe-import.service';
import { ImageSearchPanelComponent } from '../image-search-panel/image-search-panel.component';
import { AppLogger, AppLoggerService } from '../../core/logging';

/** A proposed image from an image-refetch job plus the user's review state and
 *  its preview object URL (fetched as a blob because the endpoint is
 *  auth-protected). Mirrors the import review gallery in the recipes page. */
interface RefetchImageView {
  name: string;
  role: string;
  defaultTitle: boolean;
  score?: number;
  keep: boolean;
  isTitle: boolean;
  objectUrl: string | null;
}

/**
 * Manages a recipe's images: a single gallery of photos with one marked as the
 * title (hero). It works with the `full` variant of each upload as the photo's
 * single representation — the backend also stores a thumbnail and the original,
 * but those are display/processing details the user never sees here.
 *
 * For recipes imported from a URL (see `sourceUrl`), it also offers "refetch
 * images from source": an images-only re-crawl of the original page so a recipe
 * created before the images feature can adopt images the page still hosts.
 *
 * Independently of that, "Search image" opens the shared image-search panel so
 * a picture can be picked from a stock library for any recipe.
 */
@Component({
  selector: 'app-recipe-image-manager',
  imports: [FaIconComponent, UiButtonComponent, UiModalComponent, ImageSearchPanelComponent],
  templateUrl: './recipe-image-manager.component.html',
  styleUrl: './recipe-image-manager.component.scss'
})
export class RecipeImageManagerComponent implements OnInit, OnDestroy {
  private readonly log: AppLogger;

  /** The recipe whose images are managed. Images can only be attached once the
   *  recipe exists, so callers pass this only in edit mode. */
  @Input() recipeId!: number;

  /** Source URL of an imported recipe (empty for hand-authored recipes). When
   *  set, the "refetch images from source" action is offered. */
  @Input() sourceUrl = '';

  @ViewChild('refetchModal') refetchModal?: UiModalComponent;

  images: RecipeImage[] = [];
  selectedImage: RecipeImage | null = null;
  isUploading = false;
  isDraggingOver = false;
  /** When true the search panel replaces the upload/gallery column. */
  isSearchOpen = false;

  // --- Image refetch (re-crawl the source page for images) ---
  /** Phase of the refetch flow: idle → crawling (job running) → review (images
   *  ready) → applying. */
  refetchPhase: 'idle' | 'crawling' | 'review' | 'applying' = 'idle';
  refetchError: string | null = null;
  refetchImages: RefetchImageView[] = [];
  private refetchJobId: string | null = null;
  private refetchPollTimer?: ReturnType<typeof setInterval>;

  faCheck = faCheck;
  faStar = faStar;
  faTrash = faTrash;
  faUpload = faUpload;
  faSpinner = faSpinner;
  faRotate = faRotate;
  faSearch = faMagnifyingGlass;

  constructor(
    private recipeImageService: RecipeImageService,
    private recipeImportService: RecipeImportService,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(RecipeImageManagerComponent);
  }

  ngOnInit(): void {
    if (this.recipeId) {
      this.loadImages();
    }
  }

  ngOnDestroy(): void {
    this.stopRefetchPolling();
    this.clearRefetchImages();
  }

  get canRefetch(): boolean {
    return !!this.sourceUrl;
  }

  get hasPrimary(): boolean {
    return this.images.some(img => img.is_primary);
  }

  private loadImages(restoreId?: number): void {
    this.recipeImageService.getImages(this.recipeId).subscribe({
      next: (all: RecipeImage[]) => {
        // Work with the `full` variant as the single photo representation.
        this.images = all.filter(img => img.type === 'full');

        const restored = restoreId != null ? this.images.find(i => i.id === restoreId) : undefined;
        this.selectedImage =
          restored ??
          this.images.find(i => i.is_primary) ??
          (this.images.length > 0 ? this.images[0] : null);
      },
      error: (err) => {
        this.log.error('Failed to load recipe images', err, { recipeId: this.recipeId });
        this.images = [];
        this.selectedImage = null;
      }
    });
  }

  selectImage(image: RecipeImage): void {
    this.selectedImage = image;
  }

  openSearch(): void {
    this.log.debug('Opening image search', { recipeId: this.recipeId });
    this.isSearchOpen = true;
  }

  closeSearch(): void {
    this.isSearchOpen = false;
  }

  /**
   * An imported image is stored with all its variants, so the whole list is
   * reloaded. The panel stays open so several images can be picked in one go.
   */
  onImageImported(): void {
    this.log.debug('Searched image imported', { recipeId: this.recipeId });
    this.loadImages();
  }

  /** Credit line for an image that came from an image search. */
  attributionFor(image: RecipeImage | null): string {
    const attribution = image?.attribution;
    if (!attribution) {
      return '';
    }
    return [attribution.provider, attribution.author, attribution.license]
      .filter(Boolean)
      .join(' · ');
  }

  makePrimary(): void {
    const target = this.selectedImage ?? this.images[0];
    if (!target?.id || target.is_primary) {
      return;
    }
    const targetId = target.id;

    // Optimistic: exactly one primary.
    this.images = this.images.map(img => ({ ...img, is_primary: img.id === targetId }));
    this.selectedImage = this.images.find(i => i.id === targetId) ?? null;

    this.recipeImageService.setAsPrimaryImage(targetId).subscribe({
      next: () => this.loadImages(targetId),
      error: (err) => {
        this.log.error('Failed to set recipe title image', err, { imageId: targetId });
        this.loadImages(targetId);
      }
    });
  }

  deleteImage(): void {
    const target = this.selectedImage ?? this.images[0];
    if (!target?.id) {
      return;
    }
    this.recipeImageService.deleteImage(target.id).subscribe({
      next: () => {
        this.selectedImage = null;
        this.loadImages();
      },
      error: (err) => this.log.error('Failed to delete recipe image', err, { imageId: target.id })
    });
  }

  uploadImage(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (file) {
        this.handleFileUpload(file);
      }
    };
    input.click();
  }

  private handleFileUpload(file: File): void {
    if (!file.type.match(/image\/(png|jpeg|webp)/)) {
      this.log.warn('Invalid file type for recipe image upload', { fileType: file.type });
      return;
    }
    this.isUploading = true;
    // First image becomes the title automatically (backend also enforces this).
    const makePrimary = this.images.length === 0;
    this.recipeImageService.uploadImage(this.recipeId, file, makePrimary).subscribe({
      next: () => {
        this.isUploading = false;
        this.loadImages();
      },
      error: (err) => {
        this.log.error('Failed to upload recipe image', err, { recipeId: this.recipeId });
        this.isUploading = false;
      }
    });
  }

  isUserOwned(image: RecipeImage | null): boolean {
    return (image?.owner_type ?? 'user').toLowerCase() === 'user';
  }

  /** Images the user picked from an image search. */
  isSearchOwned(image: RecipeImage | null): boolean {
    return image?.owner_type?.toLowerCase() === 'search';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
    if (this.isUploading) {
      return;
    }
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  // --- Image refetch ---------------------------------------------------------

  /** Kick off an images-only re-crawl of the recipe's source page and open the
   *  review modal. Proposed images stream in as the crawl finishes. */
  startRefetch(): void {
    if (!this.recipeId || !this.canRefetch || this.refetchPhase !== 'idle') {
      return;
    }
    this.refetchError = null;
    this.clearRefetchImages();
    this.refetchPhase = 'crawling';
    this.refetchModal?.open();

    this.recipeImportService.startImageRefetch(this.recipeId).subscribe({
      next: (res) => {
        const job = res?.job;
        if (!job?.id) {
          this.failRefetch('Could not start image refetch.');
          return;
        }
        this.refetchJobId = job.id;
        this.pollRefetchJob(job.id);
      },
      error: (err) => {
        this.log.error('Failed to start recipe image refetch', err, { recipeId: this.recipeId });
        this.failRefetch('Could not start image refetch.');
      }
    });
  }

  /** Poll the refetch job until the crawl produces images (draft_ready) or
   *  fails. Mirrors the import job polling in the recipes page. */
  private pollRefetchJob(jobId: string): void {
    this.stopRefetchPolling();

    const pollOnce = () => {
      this.recipeImportService.getImportJob(jobId).subscribe({
        next: (res) => {
          const job = res?.job;
          const status = job?.status;
          if (status === 'draft_ready' || status === 'succeeded') {
            this.stopRefetchPolling();
            this.loadRefetchImages(job!);
          } else if (status === 'failed') {
            this.stopRefetchPolling();
            this.failRefetch('The source page could not be re-crawled. Try again later.');
          }
        },
        error: (err) => this.log.error('Error polling image refetch job', err, { jobId })
      });
    };

    pollOnce();
    this.refetchPollTimer = setInterval(pollOnce, 1000);
  }

  /** Build the review state from the job's proposed images and fetch each
   *  preview as a blob. Default selection mirrors the backend: the title image
   *  is kept and marked title. */
  private loadRefetchImages(job: RecipeImportJobDto): void {
    this.clearRefetchImages();

    const proposed = job.proposed_images ?? [];
    if (proposed.length === 0) {
      this.refetchPhase = 'review';
      this.refetchImages = [];
      this.refetchError = 'That page did not offer any usable images.';
      return;
    }

    const views: RefetchImageView[] = proposed.map(p => ({
      name: p.name,
      role: p.role,
      defaultTitle: !!p.default_title,
      score: p.score,
      keep: !!p.default_title,
      isTitle: !!p.default_title,
      objectUrl: null
    }));
    // Guarantee exactly one title when the crawler didn't flag one.
    if (!views.some(v => v.isTitle) && views.length > 0) {
      views[0].keep = true;
      views[0].isTitle = true;
    }

    this.refetchImages = views;
    this.refetchPhase = 'review';
    this.refetchError = null;

    for (const view of views) {
      this.recipeImportService.getImportJobImage(job.id, view.name).subscribe({
        next: (blob) => {
          if (!blob || blob.size === 0) {
            return;
          }
          const url = URL.createObjectURL(blob);
          const target = this.refetchImages.find(v => v.name === view.name);
          if (target) {
            target.objectUrl = url;
          } else {
            URL.revokeObjectURL(url);
          }
        },
        error: (err) => this.log.error('Error loading refetch preview', err, { jobId: job.id, name: view.name })
      });
    }
  }

  /** Toggle whether a proposed image is kept. Unkeeping the title moves it to
   *  the first remaining kept image. */
  toggleRefetchKeep(name: string): void {
    const target = this.refetchImages.find(v => v.name === name);
    if (!target) {
      return;
    }
    target.keep = !target.keep;
    if (!target.keep) {
      target.isTitle = false;
    }
    if (!this.refetchImages.some(v => v.keep && v.isTitle)) {
      const firstKept = this.refetchImages.find(v => v.keep);
      if (firstKept) {
        firstKept.isTitle = true;
      }
    }
  }

  /** Mark a proposed image as the title (which also keeps it). */
  setRefetchTitle(name: string): void {
    for (const v of this.refetchImages) {
      v.isTitle = v.name === name;
      if (v.name === name) {
        v.keep = true;
      }
    }
  }

  get refetchKeptCount(): number {
    return this.refetchImages.filter(v => v.keep).length;
  }

  /** Apply the selection: ingest the kept images onto the recipe, then close
   *  the modal and reload the gallery. */
  applyRefetch(): void {
    if (!this.refetchJobId || this.refetchPhase === 'applying') {
      return;
    }
    const selection: ImportImageSelectionDto[] = this.refetchImages.map(v => ({
      name: v.name,
      keep: v.keep,
      is_title: v.keep && v.isTitle
    }));

    this.refetchPhase = 'applying';
    this.recipeImportService.applyImageRefetch(this.refetchJobId, selection).subscribe({
      next: () => {
        this.closeRefetch();
        this.loadImages();
      },
      error: (err) => {
        this.log.error('Failed to apply refetched images', err, { jobId: this.refetchJobId });
        this.refetchPhase = 'review';
        this.refetchError = 'Could not save the selected images. Please try again.';
      }
    });
  }

  /** Close the review modal and reset the refetch flow. */
  closeRefetch(): void {
    this.stopRefetchPolling();
    this.clearRefetchImages();
    this.refetchJobId = null;
    this.refetchError = null;
    this.refetchPhase = 'idle';
    this.refetchModal?.close();
  }

  /** Called when the modal is dismissed (backdrop / X) — keep our state in sync. */
  onRefetchModalClosed(): void {
    if (this.refetchPhase !== 'idle') {
      this.stopRefetchPolling();
      this.clearRefetchImages();
      this.refetchJobId = null;
      this.refetchPhase = 'idle';
    }
  }

  private failRefetch(message: string): void {
    this.stopRefetchPolling();
    this.refetchError = message;
    this.refetchPhase = 'review';
  }

  private stopRefetchPolling(): void {
    if (this.refetchPollTimer) {
      clearInterval(this.refetchPollTimer);
      this.refetchPollTimer = undefined;
    }
  }

  private clearRefetchImages(): void {
    for (const v of this.refetchImages) {
      if (v.objectUrl) {
        URL.revokeObjectURL(v.objectUrl);
      }
    }
    this.refetchImages = [];
  }
}
